<!-- #94: the personalized strips come first — for a signed-in user this is what the landing
     page is now for; the GetLocal pitch below stays for everyone (and is all an anonymous
     visitor sees, since the personalization API is [Authorize]). -->
<div class="landing-page">
  @if (loading) {
    <div class="d-flex justify-content-center py-5">
      <app-loading-spinner
        text="Empfehlungen werden geladen…"
        i18n-text="@@landingPage.loading"
      ></app-loading-spinner>
    </div>
  }

  @if (hasPersonalization) {
    <!-- Each strip is hidden when its section is empty: an area with a heading and nothing
         under it reads as a broken page, not as "nothing here yet". A section can also be
         shorter than the requested size — listings that stopped resolving drop out silently
         (backend #651, same contract as the Merkliste). -->
    @if (recentlyViewed.length > 0) {
      <section class="landing-strip" data-testid="landing-recently-viewed-section">
        <h2
          class="pb-2 border-bottom fw-light"
          data-testid="landing-recently-viewed-heading"
          i18n="@@landingPage.recentlyViewed.heading"
        >
          Zuletzt angesehen
        </h2>
        <div class="catalog-item-grid mt-4">
          @for (catalogItem of recentlyViewed; track catalogItem.catalogSku.skuId) {
            @if (catalogItem.catalogSku) {
              <app-catalog-item-card
                [catalogSku]="catalogItem.catalogSku"
                [isBookmarked]="bookmarkedIds.has(catalogItem.catalogSku.skuId)"
                [bookmarkAction]="bookmarkAction(catalogItem.catalogSku.skuId)"
              ></app-catalog-item-card>
            }
          }
        </div>
      </section>
    }

    @if (suggestions.length > 0) {
      <section class="landing-strip" data-testid="landing-suggestions-section">
        <!-- The heading comes from the component because `suggestionsAreFallback` decides it:
             the general catalog entry is labelled as such and never as personalized. The flag
             is mirrored onto the element so a test can assert that without the wording. -->
        <h2
          class="pb-2 border-bottom fw-light"
          data-testid="landing-suggestions-heading"
          [attr.data-fallback]="home.suggestionsAreFallback"
        >
          {{ suggestionsHeading }}
        </h2>
        <div class="catalog-item-grid mt-4">
          @for (catalogItem of suggestions; track catalogItem.catalogSku.skuId) {
            @if (catalogItem.catalogSku) {
              <app-catalog-item-card
                [catalogSku]="catalogItem.catalogSku"
                [isBookmarked]="bookmarkedIds.has(catalogItem.catalogSku.skuId)"
                [bookmarkAction]="bookmarkAction(catalogItem.catalogSku.skuId)"
              ></app-catalog-item-card>
            }
          }
        </div>
      </section>
    }

    <!-- The opt-in offer, right where its absence is felt: the strip above is the impersonal
         fallback precisely because personalization is off. It only LINKS to Einstellungen —
         consent (and the warning that switching it off deletes the history) lives in exactly
         one place, so the two can never drift apart. -->
    @if (showOptInHint) {
      <div class="alert alert-light border d-flex align-items-start gap-3" data-testid="landing-optin-hint">
        <div class="flex-grow-1">
          <p class="mb-1" i18n="@@landingPage.optIn.text">
            Persönliche Empfehlungen sind ausgeschaltet. Wenn Sie sie einschalten, merkt sich
            GetLocal Ihre Suchen und angesehenen Artikel, um Ihnen passende Angebote zu zeigen.
            Sie können das jederzeit wieder ausschalten.
          </p>
          <a [routerLink]="['/shop-settings']" data-testid="landing-optin-link" i18n="@@landingPage.optIn.link">
            In den Einstellungen einschalten
          </a>
        </div>
        <button
          type="button"
          class="btn-close"
          data-testid="landing-optin-dismiss-button"
          (click)="dismissOptInHint()"
          i18n-aria-label="@@landingPage.optIn.dismiss"
          aria-label="Hinweis ausblenden"
        ></button>
      </div>
    }

    <!-- #652: shown whether or not personalization is on — someone's own saved listings are a
         store, not a profiling signal (backend #651 decision 4). -->
    @if (bookmarks.length > 0) {
      <section class="landing-strip" data-testid="landing-bookmarks-section">
        <div class="d-flex align-items-center justify-content-between border-bottom pb-2">
          <h2 class="fw-light mb-0" data-testid="landing-bookmarks-heading" i18n="@@bookmarkList.heading">Merkliste</h2>
          <a [routerLink]="['/bookmark-list']" data-testid="landing-bookmarks-link" i18n="@@landingPage.bookmarks.all">
            Alle anzeigen
          </a>
        </div>
        <div class="catalog-item-grid mt-4">
          @for (catalogItem of bookmarks; track catalogItem.catalogSku.skuId) {
            @if (catalogItem.catalogSku) {
              <app-catalog-item-card
                [catalogSku]="catalogItem.catalogSku"
                [isBookmarked]="bookmarkedIds.has(catalogItem.catalogSku.skuId)"
                [bookmarkAction]="bookmarkAction(catalogItem.catalogSku.skuId)"
              ></app-catalog-item-card>
            }
          }
        </div>
      </section>
    }
  }
</div>

<!-- Bootstrap icon sprite for the feature grid below. Only the symbols that grid actually
     uses are kept; the placeholder cover cards that carried the rest were dropped with #94. -->
<svg xmlns="http://www.w3.org/2000/svg" style="display: none">
  <symbol id="people-circle" viewBox="0 0 16 16">
    <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
    <path
      fill-rule="evenodd"
      d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"
    />
  </symbol>
  <symbol id="collection" viewBox="0 0 16 16">
    <path
      d="M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm2-2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1h-7zM0 13a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 16 13V6a1.5 1.5 0 0 0-1.5-1.5h-13A1.5 1.5 0 0 0 0 6v7zm1.5.5A.5.5 0 0 1 1 13V6a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5h-13z"
    />
  </symbol>
  <symbol id="toggles2" viewBox="0 0 16 16">
    <path d="M9.465 10H12a2 2 0 1 1 0 4H9.465c.34-.588.535-1.271.535-2 0-.729-.195-1.412-.535-2z" />
    <path
      d="M6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 1a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm.535-10a3.975 3.975 0 0 1-.409-1H4a1 1 0 0 1 0-2h2.126c.091-.355.23-.69.41-1H4a2 2 0 1 0 0 4h2.535z"
    />
    <path d="M14 4a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" />
  </symbol>
  <symbol id="chevron-right" viewBox="0 0 16 16">
    <path
      fill-rule="evenodd"
      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
    />
  </symbol>
</svg>

<div class="" id="custom-cards" data-testid="landing-page">
  <div class="pt-5 mx-auto">
    <h1 class="pb-2 border-bottom fw-light" i18n>GetLocal</h1>
  </div>

  <div class="px-4 py-2" id="featured-3">
    <h2 class="pb-2 border-bottom" i18n>Vielfalt, in Deiner Nähe!</h2>
    <div class="row g-4 py-5 row-cols-1 row-cols-lg-3">
      <div class="feature col">
        <div class="feature-icon bg-primary bg-gradient">
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#collection" />
          </svg>
        </div>
        <h2 i18n>App</h2>
        <p i18n>
          GetLocal ist eine App, die kostenlos heruntergeladen werden kann und eine regionale (und
          natürlich auch überregionale) Suche nach Produkten und Dienstleistungen aller Art
          ermöglicht.
        </p>
        <a href="#" class="icon-link" i18n>
          Call to action
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#chevron-right" />
          </svg>
        </a>
      </div>
      <div class="feature col">
        <div class="feature-icon bg-primary bg-gradient">
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#people-circle" />
          </svg>
        </div>
        <h2 i18n>Geräte</h2>
        <p i18n>GetLocal gibt es für alle gängigen Geräte wie Android, IPad, IPhone und im Browser!</p>
        <a href="#" class="icon-link" i18n>
          Call to action
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#chevron-right" />
          </svg>
        </a>
      </div>
      <div class="feature col">
        <div class="feature-icon bg-primary bg-gradient">
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#toggles2" />
          </svg>
        </div>
        <h2 i18n>Produktscan</h2>
        <p i18n>Scanne einen Barcode oder QR-Code um ein Produkt mit wenigen Handgriffen anzubieten!</p>
        <a href="#" class="icon-link" i18n>
          Call to action
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#chevron-right" />
          </svg>
        </a>
      </div>
      <div class="feature col">
        <div class="feature-icon bg-primary bg-gradient">
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#toggles2" />
          </svg>
        </div>
        <h2 i18n>Kostenlos</h2>
        <p i18n>
          Für Käufer ist die App uneingeschränkt kostenlos. Anbieter und Ladenbesitzer können Ihre
          Produkte und Dienstleistungen für eine geringe Gebühr einem breiten Publikum bekanntgeben.
        </p>
        <a href="#" class="icon-link" i18n>
          Call to action
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#chevron-right" />
          </svg>
        </a>
      </div>
      <div class="feature col">
        <div class="feature-icon bg-primary bg-gradient">
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#toggles2" />
          </svg>
        </div>
        <h2 i18n>Fairer, nachhaltiger Handel</h2>
        <p i18n>
          Gut für die Umwelt, Einzelhandel und Verbraucher. Du kannst Produkte aus Deiner Umgebung
          suchen und direkt reservieren, um Sie persönlich im Laden abzuholen. Die umständliche
          Suche und das nachtelefonieren nach verfügbaren Produkten ist mit GetLocal Vergangenheit.
        </p>
        <a href="#" class="icon-link" i18n>
          Call to action
          <svg class="bi" width="1em" height="1em">
            <use xlink:href="#chevron-right" />
          </svg>
        </a>
      </div>
    </div>
  </div>

  <img src="../../assets/images/AdobeStock_246803607.jpeg" class="img-fluid" />
</div>
